import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { ExportableCart } from 'src/types';
import { exportCartToExcel, exportCartToPdf } from 'src/utils';
import { ModalStyled } from '../ModalStyled';
import { SimpleSpinner } from '../SimpleSpinner';
import { ToastStyled } from '../ToastStyled';

type Props = {
  show: boolean;
  setShow: (value: boolean) => void;
  getExportCart: () => ExportableCart;
};

export const ExportCartModal = ({ show, setShow, getExportCart }: Props) => {
  const [pdfLoading, setPdfLoading] = useState<boolean>(false);
  const { t, i18n } = useTranslation();

  const handleExcel = () => {
    exportCartToExcel(getExportCart());
    setShow(false);
  };

  const handlePdf = async () => {
    setPdfLoading(true);
    try {
      await exportCartToPdf(getExportCart(), {
        locale: i18n.language,
        cartName: t('export_cart.pdf_cart_name'),
        cartId: t('export_cart.pdf_cart_id'),
        created: t('export_cart.pdf_created'),
        pricesAsOf: t('export_cart.pdf_prices_as_of'),
        vendor: t('export_cart.pdf_vendor'),
        item: t('export_cart.pdf_item'),
        id: t('export_cart.pdf_id'),
        sku: t('export_cart.pdf_sku'),
        qty: t('export_cart.pdf_qty'),
        unit: t('export_cart.pdf_unit'),
        total: t('export_cart.pdf_total'),
        page: (page, total) => t('export_cart.pdf_page', { page, total }),
      });
      setShow(false);
    } catch (err) {
      console.error(err);
      toast.custom((toastBlock) => (
        <ToastStyled
          variant='error'
          toastTitle={t('export_cart.pdf_error_title')}
          handleClose={() => toast.dismiss(toastBlock)}
        >
          {t('export_cart.pdf_error')}
        </ToastStyled>
      ));
    } finally {
      setPdfLoading(false);
    }
  };

  return (
    <ModalStyled
      open={show}
      onClose={() => !pdfLoading && setShow(false)}
      customMaxWidth={440}
      aria-describedby={t('export_cart.description')}
    >
      <div className='flex flex-col items-center justify-center gap-4 p-4 sm:py-6 md:gap-6 md:px-6 md:py-8'>
        <div className='flex flex-col items-center justify-center gap-2 md:gap-3'>
          <h3 className='heading-300 text-center'>{t('export_cart.title')}</h3>
          <p className='text-200 text-center'>{t('export_cart.description')}</p>
        </div>

        <div className='flex w-full flex-col flex-nowrap items-center gap-4 sm:flex-row'>
          <button
            type='button'
            onClick={handleExcel}
            disabled={pdfLoading}
            className='button-secondary h-12 w-full sm:flex-1'
          >
            {t('export_cart.excel')}
          </button>
          <button
            type='button'
            onClick={handlePdf}
            disabled={pdfLoading}
            className='button-primary relative flex h-12 w-full items-center justify-center gap-2 disabled:opacity-100 sm:flex-1'
          >
            {pdfLoading && (
              <SimpleSpinner
                customPosition='relative'
                customWidth='w-4'
                customHeight='h-4'
                customX='left-0'
                customY='top-0'
                customColor='border-white'
              />
            )}
            {t('export_cart.pdf')}
          </button>
        </div>
      </div>
    </ModalStyled>
  );
};
